import { Injectable, inject } from "@angular/core";
import moment from "moment";
import { Observable, firstValueFrom } from "rxjs";
import { map, tap } from "rxjs/operators";
import { ArbetsorderHandelssortimentsgruppDTO } from "~app/common/models/planera/ArbetsorderHandelssortimentsgruppDTO";
import { ITypSelectValue } from "~app/common/models/selectValueModel";
import { ApiClientService } from "~app/common/services/api-client.service";
import { CommandService, ICommand } from "~app/common/services/cqs/command.service";
import { ArrayUtils } from "~app/common/utils/array-utils";
import { IPlaneraBasdataResponse } from "~app/features/planera/model/IPlaneraBasdataResponse";
import { IProduktionsomraden } from "~app/features/planera/model/IProduktionsomraden";
import { RegelverkViolService, Vardegrupper } from "~app/features/regelverk-viol/regelverk-viol.service";
import { ForkalkylsmallDTO } from "../forkalkylsmallar/models/ForkalkylsmallDTO";
import { ForkalkylsmallHandelssortimentDTO } from "../forkalkylsmallar/models/ForkalkylsmallHandelssortimentDTO";
import { ForkalkylsmallQueryResponseDTO } from "../forkalkylsmallar/models/ForkalkylsmallQueryResponseDTO";
import { TilldelaHandelssortimentsGruppDTO } from "../forkalkylsmallar/models/TilldelaHandelssortimentsGruppDTO";

@Injectable({
    providedIn: "root"
})
export class ForkalkylsmallarService {
    private readonly regelverkService = inject(RegelverkViolService);
    private readonly apiClient = inject(ApiClientService);
    private readonly commandService = inject(CommandService);

    get mattslaglist(): Observable<ITypSelectValue[]> {
        return this.regelverkService.getVarden(Vardegrupper.vkMattslag);
    }
    get handelssortimentGruppLista(): Observable<TilldelaHandelssortimentsGruppDTO[]> {
        return this.apiClient.get<TilldelaHandelssortimentsGruppDTO[]>("mina/forkalkylsmallar/GetAllTilldelaHandelssortimentsGrupper");
    }

    handelssortimentGruppListaPromise(): Promise<TilldelaHandelssortimentsGruppDTO[]> {
        return firstValueFrom(this.handelssortimentGruppLista);
    }

    updateMall(forkalkylsmall: ForkalkylsmallDTO): Observable<any> {
        this.ensureCorrectValues(forkalkylsmall);
        const cmd = Object.assign({ $type: "SaveForkalkylsmallarCommand" }, forkalkylsmall) as ICommand;
        this.commandService.addTransactionCommand(cmd);
        return this.commandService.pushCommandQueueTransaction().pipe(
            tap(() => this.commandService.reset())
        );
    }

    // Säkerställer att värdena som kommer in för att spara en mall inte har null/undefined för heltalen
    ensureCorrectValues(forkalkylsmall: ForkalkylsmallDTO) {
        const propertiesToCheck = [
            "fsc",
            "pefc",
            "grundpris",
            "tackningsbidrag",
            "tillaggAvdrag"
        ];
        if (forkalkylsmall && forkalkylsmall.handelssortiment) {
            forkalkylsmall.handelssortiment.forEach(hs => {
                propertiesToCheck.forEach(prop => {
                    this.ensureCorrectValue(hs, prop);
                });
            });
        }
    }

    ensureCorrectValue(hs: ForkalkylsmallHandelssortimentDTO, prop: string) {
        if (!this.hasIntValue(hs[prop])) {
            hs[prop] = 0;
        }
    }

    hasIntValue(value): boolean {
        if (typeof value === "number") {
            if (value <= 0) {
                return true;
            }
            if (value > 0) {
                return true;
            }
        }
        return false;
    }

    deleteMall(forkalkylsmall: ForkalkylsmallDTO): Observable<any> {
        const cmd = {
            $type: "DeleteForkalkylsmallarCommand",
            forkalkylsmallid: forkalkylsmall.id
        } as ICommand;
        this.commandService.addTransactionCommand(cmd);

        return this.commandService.pushCommandQueueTransaction().pipe(
            tap(() => this.commandService.reset())
        );
    }

    getAllMallar(): Observable<ForkalkylsmallQueryResponseDTO[]> {
        return this.apiClient.get<ForkalkylsmallQueryResponseDTO[]>("mina/forkalkylsmallar");
    }

    getAllProduktionsOmraden(): Observable<IProduktionsomraden[]> {
        return this.apiClient.get<IPlaneraBasdataResponse>("Planera/GetPlaneraBasdata", true).pipe(
            map(x => x.produktionsomraden)
        );
    }

    getHSByProduktionsOmraden(produktionsomradeId: number): Observable<ArbetsorderHandelssortimentsgruppDTO[]> {
        const params = {
            produktionsomradeId: produktionsomradeId,
            periodFrom: moment().startOf("month").toISOString(),
            periodTom: moment().add(1, "month").startOf("month").toISOString()
        };
        return this.apiClient.get<ArbetsorderHandelssortimentsgruppDTO[]>("Planera/GetHandelssortimentForProduktionsomrade", true, params);
    }

    orderBy(value1: any, value2: any, desc = false): number {
        return ArrayUtils.orderBy(value1, value2, desc);
    }
}
